import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Checkbox, Typography } from '@mui/material';
import FormControlLabel from '@mui/material/FormControlLabel';
import Text from '../text';
import CONSTANTS from '../../utils/constants/UIConstants';

export interface ExportFieldOption {
	id: string;
	colName: string;
	colType: string;
	both: boolean;
	note?: string;
 	outputId?: string | number;
	inputId?: string | number;
	testKit?: number;
}

interface ExportFieldsChecklistProps {
	format: 'long' | 'table';
	fields: ExportFieldOption[];
	onChange?: (selected: Record<string, boolean>) => void;
	selectedValues?: Record<string, boolean>;
}

const TYPE_GROUPS: { type: string; label: string }[] = [
	{ type: 'Sample', label: 'SAMPLE' },
	{ type: 'Input', label: 'INPUTS' },
	{ type: 'Output', label: 'RESULTS' },
];

const LOCKED_FIELDS = ['Site', 'Timestamp'];
const EXCLUDED_FIELDS = ['Test ID'];

const ExportFieldsChecklist: React.FC<ExportFieldsChecklistProps> = ({
	format,
	fields,
	onChange,
	selectedValues,
}) => {
	const visibleFields = useMemo(
		() => fields.filter((field) => !EXCLUDED_FIELDS.includes(field.colName)),
		[fields]
	);

	const selectedSnapshot = selectedValues ?? {};
	const selectedCount = visibleFields.filter((field) => !!selectedSnapshot[field.id]).length;
	const totalCount = visibleFields.length;

	const handleToggle = (id: string) => {
		if (!selectedValues) {
			onChange?.({ [id]: true });
			return;
		}

		const next = {
			...selectedValues,
			[id]: !selectedSnapshot[id],
		};
		onChange?.(next);
	};

	const columnsByType = useMemo(() => {
		return visibleFields.reduce((acc, field) => {
			acc[field.colType] = acc[field.colType] || [];
			acc[field.colType].push(field);
			return acc;
		}, {} as Record<string, ExportFieldOption[]>);
	}, [visibleFields]);

	return (
		<Box
			sx={{
				border: 1,
				borderColor: CONSTANTS.BORDER_COLOR,
				borderRadius: '8px',
				p: 3,
			}}
		>
			<div className="flex items-start justify-between">
				<div>
					<Text addStyle="!text-[#2C2C2C] !font-inter !text-[16px] !font-semibold">
						Fields
					</Text>
					<Text addStyle="!text-[#8A97A0] !font-jost !text-[13px] !italic !mt-[2px]">
						Saved for you across every site.
					</Text>
				</div>
				<Typography sx={{ fontSize: '13px', color: '#8A97A0', whiteSpace: 'nowrap', pt: '2px' }}>
					{selectedCount} of {totalCount} selected
				</Typography>
			</div>

			<div className="flex flex-wrap gap-2 mt-4">
				{LOCKED_FIELDS.map((label) => (
					<Box
						key={label}
						sx={{
							display: 'flex',
							alignItems: 'center',
							gap: '6px',
							bgcolor: '#F2F4F5',
							borderRadius: '6px',
							px: 1.5,
							py: 0.75,
						}}
					>
						<Checkbox
							checked
							disabled
							size="small"
							sx={{
								p: 0,
								'&.Mui-disabled': { color: '#8A97A0' },
							}}
						/>
						<Typography sx={{ fontSize: '14px', color: '#556670' }}>
							{label} <span style={{ color: '#8A97A0' }}>— always included</span>
						</Typography>
					</Box>
				))}
			</div>

			<Box
				sx={{
					display: 'grid',
					gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
					columnGap: 4,
					rowGap: 3,
					mt: 3,
				}}
			>
				{TYPE_GROUPS.map(({ type, label }) => {
					const cols = columnsByType[type] || [];
					if (cols.length === 0) return null;
					return (
						<div key={type}>
							<Typography
								sx={{
									fontSize: '12px',
									fontWeight: 600,
									letterSpacing: '0.06em',
									color: '#8A97A0',
									textTransform: 'uppercase',
									mb: 1,
								}}
							>
								{label}
							</Typography>
							{cols.map((field) => (
								<div key={field.id}>
									<FormControlLabel
										control={
											<Checkbox
												checked={!!selectedSnapshot[field.id]}
												onChange={() => handleToggle(field.id)}
												size="small"
												sx={{
													color: '#B0B8BD',
													'&.Mui-checked': { color: '#0092C8' },
												}}
											/>
										}
										label={
											<span style={{ fontSize: '14px', color: '#2C2C2C' }}>
												{field.colName}
												{field.note && (
													<span style={{ color: '#8A97A0' }}> ({field.note})</span>
												)}
											</span>
										}
									/>
								</div>
							))}
						</div>
					);
				})}
			</Box>
		</Box>
	);
};

export default ExportFieldsChecklist;

